import { Schema, DOMParser, DOMSerializer } from 'prosemirror-model'
import { addListNodes } from 'prosemirror-schema-list'
import { tableNodes } from 'prosemirror-tables'
import { sanitizeHtml, htmlAttributes } from './html'

// HTML 문자열을 DB에 저장하는 기존 계약을 유지한다. 스타일·클래스·이미지 크기 등은
// htmlAttrs에 보존하므로 기존 게시글의 주요 서식을 그대로 다시 편집할 수 있다.
const attrs = { htmlAttrs: { default: {} } }
const readAttrs = dom => ({ htmlAttrs: htmlAttributes(dom) })

function inlineAttrs (dom) {
  const chain = []
  for (let current = dom; current && /^(SPAN|FONT|STRONG|EM|B|I|U|S|STRIKE|DEL|A|SUB|SUP|CODE)$/.test(current.nodeName); current = current.parentElement) {
    if (/^(SPAN|FONT)$/.test(current.nodeName)) chain.unshift(current)
  }
  const style = document.createElement('span').style
  chain.forEach(element => {
    if (element.nodeName === 'FONT') {
      if (element.getAttribute('color')) style.color = element.getAttribute('color')
      if (element.getAttribute('face')) style.fontFamily = element.getAttribute('face')
      const size = ['', '8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'][Number(element.getAttribute('size'))]
      if (size) style.fontSize = size
    }
    const ownStyle = document.createElement('span').style
    ownStyle.cssText = element.getAttribute('style') || ''
    Array.from(ownStyle).forEach(name => style.setProperty(name, ownStyle.getPropertyValue(name)))
  })
  // 의미 서식은 별도 mark로 관리해 굵게/기울임 해제가 정상 작동하게 한다.
  if (/^(bold(er)?|[6-9]00)$/.test(style.fontWeight)) style.removeProperty('font-weight')
  if (style.fontStyle === 'italic') style.removeProperty('font-style')
  if (/underline|line-through/.test(style.textDecoration)) style.removeProperty('text-decoration')
  const htmlAttrs = htmlAttributes(dom, ['style', 'face', 'size', 'color'])
  if (style.cssText) htmlAttrs.style = style.cssText
  return { htmlAttrs }
}
const block = tag => ({
  group: 'block',
  content: 'inline*',
  attrs,
  parseDOM: [{ tag, getAttrs: readAttrs }],
  toDOM: node => [tag, node.attrs.htmlAttrs, 0]
})
const mark = (tag, alternatives = []) => ({
  attrs,
  parseDOM: [tag].concat(alternatives).map(name => ({ tag: name, getAttrs: readAttrs })),
  toDOM: value => [tag, value.attrs.htmlAttrs, 0]
})

const nodes = {
  doc: { content: 'block+' },
  paragraph: block('p'),
  text: { group: 'inline' },
  heading: {
    content: 'inline*',
    group: 'block',
    defining: true,
    attrs: { ...attrs, level: { default: 1 } },
    parseDOM: [1, 2, 3, 4, 5, 6].map(level => ({ tag: 'h' + level, getAttrs: dom => ({ ...readAttrs(dom), level }) })),
    toDOM: node => ['h' + node.attrs.level, node.attrs.htmlAttrs, 0]
  },
  blockquote: { ...block('blockquote'), content: 'block+', defining: true },
  code_block: {
    ...block('pre'),
    content: 'text*',
    marks: '',
    code: true,
    defining: true,
    parseDOM: [{ tag: 'pre', preserveWhitespace: 'full', getAttrs: readAttrs }],
    toDOM: node => ['pre', node.attrs.htmlAttrs, ['code', 0]]
  },
  horizontal_rule: { group: 'block', attrs, parseDOM: [{ tag: 'hr', getAttrs: readAttrs }], toDOM: node => ['hr', node.attrs.htmlAttrs] },
  hard_break: { inline: true, group: 'inline', selectable: false, parseDOM: [{ tag: 'br' }], toDOM: () => ['br'] },
  anchor: {
    inline: true,
    group: 'inline',
    atom: true,
    attrs,
    parseDOM: [{ tag: 'a[id]:empty', priority: 60, getAttrs: readAttrs }, { tag: 'a[name]:empty', priority: 60, getAttrs: readAttrs }, { tag: 'span[id]:empty', priority: 60, getAttrs: readAttrs }],
    toDOM: node => ['a', node.attrs.htmlAttrs]
  },
  image: {
    inline: true,
    group: 'inline',
    draggable: true,
    attrs,
    parseDOM: [{ tag: 'img[src]', getAttrs: readAttrs }],
    toDOM: node => ['img', node.attrs.htmlAttrs]
  },
  iframe: {
    inline: true,
    group: 'inline',
    atom: true,
    draggable: true,
    attrs,
    parseDOM: [{ tag: 'iframe[src]', getAttrs: readAttrs }],
    toDOM: node => ['iframe', node.attrs.htmlAttrs]
  },
  media: {
    inline: true,
    group: 'inline',
    atom: true,
    draggable: true,
    attrs: { ...attrs, tag: { default: 'video' }, sources: { default: [] } },
    parseDOM: ['video', 'audio'].map(tag => ({ tag, getAttrs: dom => ({ ...readAttrs(dom), tag, sources: Array.from(dom.querySelectorAll('source')).map(source => htmlAttributes(source)) }) })),
    toDOM: node => [node.attrs.tag, node.attrs.htmlAttrs].concat(node.attrs.sources.map(source => ['source', source]))
  },
  // div 묶음과 이미지 figure는 블록 계층으로 보존한다.
  container: {
    group: 'block',
    content: 'block+',
    defining: true,
    attrs: { ...attrs, tag: { default: 'div' } },
    parseDOM: ['div', 'section', 'article', 'figure', 'figcaption', 'address', 'header', 'footer'].map(tag => ({ tag, getAttrs: dom => ({ ...readAttrs(dom), tag }) })),
    toDOM: node => [node.attrs.tag, node.attrs.htmlAttrs, 0]
  },
  details: {
    group: 'block',
    content: 'summary block+',
    defining: true,
    attrs,
    parseDOM: [{ tag: 'details', getAttrs: readAttrs }],
    toDOM: node => ['details', node.attrs.htmlAttrs, 0]
  },
  summary: { ...block('summary'), group: undefined, defining: true }
}

const marks = {
  link: { ...mark('a'), inclusive: false },
  bold: { ...mark('strong', ['b']), parseDOM: [{ tag: 'strong', getAttrs: readAttrs }, { tag: 'b', getAttrs: readAttrs }, { style: 'font-weight', getAttrs: value => /^(bold(er)?|[6-9]00)$/.test(value) ? {} : false }] },
  italic: { ...mark('em', ['i']), parseDOM: [{ tag: 'em', getAttrs: readAttrs }, { tag: 'i', getAttrs: readAttrs }, { style: 'font-style=italic' }] },
  underline: { ...mark('u'), parseDOM: [{ tag: 'u', getAttrs: readAttrs }, { style: 'text-decoration', getAttrs: value => value.indexOf('underline') !== -1 ? {} : false }] },
  strike: { ...mark('s', ['strike', 'del']), parseDOM: [{ tag: 's', getAttrs: readAttrs }, { tag: 'del', getAttrs: readAttrs }, { tag: 'strike', getAttrs: readAttrs }, { style: 'text-decoration', getAttrs: value => value.indexOf('line-through') !== -1 ? {} : false }] },
  subscript: mark('sub'),
  superscript: mark('sup'),
  code: { ...mark('code'), code: true },
  // span의 글자 크기/색/배경색/글꼴과 legacy font 태그까지 보존한다.
  text_style: {
    attrs,
    parseDOM: [{ tag: 'span', getAttrs: inlineAttrs }, { tag: 'font', getAttrs: inlineAttrs }],
    toDOM: value => ['span', value.attrs.htmlAttrs, 0]
  }
}

const schema = new Schema({ nodes, marks })
let extendedNodes = addListNodes(schema.spec.nodes, 'paragraph block*', 'block')
;['bullet_list', 'ordered_list', 'list_item'].forEach(name => {
  const spec = extendedNodes.get(name)
  const tag = { bullet_list: 'ul', ordered_list: 'ol', list_item: 'li' }[name]
  extendedNodes = extendedNodes.update(name, {
    ...spec,
    attrs: { ...spec.attrs, ...attrs },
    parseDOM: [{ tag, getAttrs: dom => ({ ...readAttrs(dom), ...(name === 'ordered_list' ? { order: Number(dom.getAttribute('start')) || 1 } : {}) }) }],
    toDOM: node => [tag, { ...node.attrs.htmlAttrs, ...(name === 'ordered_list' && node.attrs.order !== 1 ? { start: node.attrs.order } : {}) }, 0]
  })
})
const tables = tableNodes({ tableGroup: 'block',
  cellContent: 'block+',
  cellAttributes: {
    htmlAttrs: { default: {}, getFromDOM: dom => htmlAttributes(dom, ['colspan', 'rowspan', 'data-colwidth']), setDOMAttr: (value, output) => Object.assign(output, value) }
  } })
// 병합/분할 모델을 사용하되 테이블과 행의 HTML 속성도 보존한다.
;['table', 'table_row'].forEach(name => {
  const tag = name === 'table' ? 'table' : 'tr'
  tables[name] = {
    ...tables[name],
    attrs,
    parseDOM: [{ tag, getAttrs: readAttrs }],
    toDOM: node => name === 'table' ? ['table', node.attrs.htmlAttrs, ['tbody', 0]] : ['tr', node.attrs.htmlAttrs, 0]
  }
})
// TinyMCE 표의 caption/colgroup은 편집 불가능한 메타데이터로 보존한다.
// 셀 크기를 편집하면 새 colwidth를 우선하여 colgroup을 다시 생성한다.
tables.table = {
  ...tables.table,
  attrs: { ...attrs, caption: { default: '' }, columns: { default: '' } },
  parseDOM: [{
    tag: 'table',
    getAttrs: dom => ({ ...readAttrs(dom), caption: Array.from(dom.children).filter(child => child.nodeName === 'CAPTION').map(child => child.outerHTML).join(''), columns: Array.from(dom.children).filter(child => child.nodeName === 'COLGROUP').map(child => child.outerHTML).join('') }),
    contentElement: dom => {
      const content = dom.cloneNode(true)
      Array.from(content.children).filter(child => ['CAPTION', 'COLGROUP'].indexOf(child.nodeName) !== -1).forEach(child => content.removeChild(child))
      return content
    }
  }],
  toDOM: node => {
    const table = document.createElement('table')
    Object.keys(node.attrs.htmlAttrs).forEach(name => table.setAttribute(name, node.attrs.htmlAttrs[name]))
    table.innerHTML = node.attrs.caption + node.attrs.columns
    const widths = []
    if (node.firstChild) {
      node.firstChild.forEach(cell => {
        for (let index = 0; index < cell.attrs.colspan; index++) widths.push(cell.attrs.colwidth ? cell.attrs.colwidth[index] : 0)
      })
    }
    if (widths.some(width => width > 0)) {
      const oldColumns = table.querySelector('colgroup')
      if (oldColumns) table.removeChild(oldColumns)
      const colgroup = document.createElement('colgroup')
      widths.forEach(width => {
        const col = document.createElement('col')
        if (width) col.style.width = width + 'px'
        colgroup.appendChild(col)
      })
      table.appendChild(colgroup)
    }
    const tbody = table.appendChild(document.createElement('tbody'))
    return { dom: table, contentDOM: tbody }
  }
}
extendedNodes = extendedNodes.append(tables)
export const editorSchema = new Schema({ nodes: extendedNodes, marks: schema.spec.marks })

export function parseHtml (html) {
  const wrapper = document.createElement('div')
  wrapper.innerHTML = sanitizeHtml(html)
  return DOMParser.fromSchema(editorSchema).parse(wrapper)
}

export function serializeHtml (doc) {
  // 빈 에디터는 기존 필수값 검사와 호환되도록 빈 문자열로 전달한다.
  if (doc.childCount === 1 && doc.firstChild.type.name === 'paragraph' && !doc.firstChild.content.size && !Object.keys(doc.firstChild.attrs.htmlAttrs).length) return ''
  const wrapper = document.createElement('div')
  wrapper.appendChild(DOMSerializer.fromSchema(editorSchema).serializeFragment(doc.content))
  return sanitizeHtml(wrapper.innerHTML)
}



// WEBPACK FOOTER //
// ./src/components/ETC/editor/schema.js